/**
 * api/skills.js — queries para o Catálogo de Skills.
 *
 * Endpoints:
 *   GET /skills                     — lista com filtros e paginação
 *     query: source?, category?, search?, limit?, offset?
 *   GET /skills/{id}                — detalhe de uma skill
 *
 * Total do catálogo: 78 skills (17 Anthropic + 28 Tech Leads Club + 33 Marketing).
 *
 * Convenções:
 *   - skillKeys seguem hierarquia: ['skills', filtros?, id?]
 *   - staleTime = 5 min (catálogo muda raramente em produção).
 *   - ApiError 403 → ForbiddenBanner.
 *   - Clean: sem lógica de UI.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client';

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const skillKeys = {
  all:      ()                 => ['skills'],
  list:     (filters)          => ['skills', 'list', filters],
  detail:   (id)               => ['skills', id],
};

// ─── Queries ──────────────────────────────────────────────────────────────────

/**
 * useSkills — lista paginada de skills com filtros.
 *
 * @param {{
 *   source?: string,
 *   category?: string,
 *   search?: string,
 *   limit?: number,
 *   offset?: number,
 *   sem_conteudo?: boolean   // A2 FR-22c: só skills sem conteúdo
 * }} filters
 */
export function useSkills(filters = {}) {
  const api = useApi();

  const params = new URLSearchParams();
  if (filters.source)         params.set('source',   filters.source);
  if (filters.category)       params.set('category', filters.category);
  if (filters.search)         params.set('search',   filters.search);
  if (filters.limit != null)  params.set('limit',    String(filters.limit));
  if (filters.offset != null) params.set('offset',   String(filters.offset));
  if (filters.sem_conteudo)   params.set('sem_conteudo', 'true');
  const qs = params.toString();

  return useQuery({
    queryKey: skillKeys.list(filters),
    queryFn:  () => api.get(`/skills${qs ? `?${qs}` : ''}`),
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
}

/**
 * useSkill — detalhe de uma única skill.
 * @param {string|null} id
 */
export function useSkill(id) {
  const api = useApi();
  return useQuery({
    queryKey: skillKeys.detail(id),
    queryFn:  () => api.get(`/skills/${id}`),
    enabled:  Boolean(id),
    staleTime: 5 * 60_000,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

/**
 * useCreateSkill — cria uma nova skill.
 *
 * POST /skills body { name, description, content?, source?, category?, metadata? }.
 * `metadata` marca a skill como tool executável, no shape:
 *   { is_tool: true, tool: { name, description, parameters, executor, mcp_server_id } }
 * O body inteiro é encaminhado, então basta o chamador incluir `metadata`.
 * Ao ter sucesso invalida a chave raiz ['skills'].
 */
export function useCreateSkill() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/skills', body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: skillKeys.all() }),
  });
}

/**
 * useUpdateSkill — edita uma skill existente (body parcial).
 *
 * PUT /skills/{name}. O body inteiro é encaminhado, incluindo `metadata`
 * quando a skill é marcada/desmarcada como tool.
 * Ao ter sucesso invalida a chave raiz ['skills'].
 *
 * @example
 *   const update = useUpdateSkill();
 *   update.mutate({ name, body: { description, content, category, metadata } });
 */
export function useUpdateSkill() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: ({ name, body }) => api.put(`/skills/${name}`, body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: skillKeys.all() }),
  });
}

/**
 * useDeleteSkill — exclui uma skill (requer perfil administer).
 *
 * DELETE /skills/{name}. Ao ter sucesso invalida a chave raiz ['skills'].
 */
export function useDeleteSkill() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: ({ name }) => api.delete(`/skills/${name}`),
    onSuccess:  () => qc.invalidateQueries({ queryKey: skillKeys.all() }),
  });
}

/**
 * useImportSkill — importa uma skill a partir de uma URL.
 *
 * POST /skills/import body { url, name? }. Invalida a chave raiz ['skills'].
 */
export function useImportSkill() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/skills/import', body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: skillKeys.all() }),
  });
}

/**
 * useImportSkillFile — importa uma skill de um ARQUIVO (.md ou .zip) — A1 FR-26.
 *
 * POST /skills/import-file?filename=<nome>&name=<opcional> com o arquivo CRU no corpo
 * (não é multipart). Content-Type: application/zip · text/markdown · application/octet-stream.
 * 201 SkillOut & { tem_conteudo, arquivos } · 413 limite · 422 frontmatter/caminho/segredo.
 */
export function useImportSkillFile() {
  const api = useApi();
  const qc  = useQueryClient();
  return useMutation({
    mutationFn: ({ file, name }) => {
      const filename = file?.name || 'skill.md';
      const params = new URLSearchParams({ filename });
      if (name) params.set('name', name);
      const lower = filename.toLowerCase();
      const contentType = lower.endsWith('.zip') ? 'application/zip'
        : lower.endsWith('.md') ? 'text/markdown'
          : 'application/octet-stream';
      return api.request(`/skills/import-file?${params.toString()}`, {
        method: 'POST',
        body: file,
        headers: { 'Content-Type': contentType },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: skillKeys.all() }),
  });
}
